/**
 * # Overlay Manager
 *
 * Replaces Chakra v3 `createOverlay` with a hook-based system that:
 * - Synchronizes overlays: opening one overlay closes all others (exclusive mode by default)
 * - Integrates with Lenis scroll locking
 * - Supports lazy loading via `loadable()`
 *
 * ## Why not Chakra's `createOverlay`?
 *
 * | Concern             | `createOverlay`                            | Overlay Manager                                      |
 * |---------------------|--------------------------------------------|------------------------------------------------------|
 * | Synchronization     | No built-in: each overlay is independent   | Opening any overlay closes all others automatically  |
 * | API style           | Static singleton: `.open(id, props)`       | Hook: `const {open} = useMyOverlay()`                |
 * | Viewport            | Per-type `<overlay.Viewport />` in tree    | Single `<OverlayProvider>` at app level              |
 * | Route cleanup       | Manual `overlay.removeAll()` on navigate   | Automatic via `OverlayViewport` `useEffect`          |
 * | Scroll lock         | Not Lenis-aware — breaks smooth scroll     | `<OverlayScrollLock>` inside `LenisProvider`         |
 * | Lazy loading        | Component must be imported eagerly         | Use `loadable()` — chunk only loaded on first open   |
 *
 * ## Usage pattern
 * ```jsx
 * // myOverlay/index.jsx
 * const Loadable = loadable(() => import('./internal'))
 * export const {useOverlay: useMyOverlay, useIsOpen: useIsMyOverlayOpen} = createOverlayType(Loadable)
 *
 * // consumer
 * const {open} = useMyOverlay()
 * open({title: 'Hello'})  // lazy-loads ./internal on first call
 * ```
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState
} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import useLenisScrollLock from '../hooks/use-lenis-scroll-lock'

const OPEN = 'OPEN'
const CLOSE = 'CLOSE'
const REMOVE = 'REMOVE'
const CLOSE_ALL = 'CLOSE_ALL'
const REMOVE_ALL = 'REMOVE_ALL'

/**
 * @typedef {{ id: string, Component: React.ComponentType, props: Object, open: boolean }} OverlayEntry
 */

/**
 * Map-based reducer for O(1) overlay state updates.
 * @param {Map<string, OverlayEntry>} state
 * @param {{ type: string, id?: string, Component?: React.ComponentType, props?: Object, exclusive?: boolean }} action
 * @returns {Map<string, OverlayEntry>}
 */
const overlaysReducer = (state, action) => {
    switch (action.type) {
        case OPEN: {
            const {id, Component, props, exclusive} = action
            // exclusive mode: close all other open overlays before opening this one
            const base = exclusive
                ? new Map(
                      [...state.entries()].map(([k, v]) => [
                          k,
                          k !== id && v.open ? {...v, open: false} : v
                      ])
                  )
                : new Map(state)
            base.set(id, {id, Component, props, open: true})
            return base
        }
        case CLOSE: {
            if (!state.has(action.id)) return state
            const next = new Map(state)
            next.set(action.id, {...state.get(action.id), open: false})
            return next
        }
        case REMOVE: {
            if (!state.has(action.id)) return state
            const next = new Map(state)
            next.delete(action.id)
            return next
        }
        case CLOSE_ALL:
            return new Map([...state.entries()].map(([k, v]) => [k, {...v, open: false}]))
        case REMOVE_ALL:
            return new Map()
        default:
            return state
    }
}

/** Stable dispatch context — never triggers re-renders on its own. */
const OverlayDispatchContext = createContext(null)
/** State context — re-renders subscribers when overlay map changes. */
const OverlayStateContext = createContext(null)

/**
 * Renders a single overlay, suppressing `open` until after SSR hydration.
 * Bridges `onOpenChange` and `onExitComplete` to dispatch close/remove actions.
 * Calls `overlayProps.onClose({source})` on every close path:
 *   - `'close'` — user interaction or programmatic `close()` via `onOpenChange`
 *   - `'remove'` — programmatic `remove()` / `removeAll()` while the overlay was open
 * @param {{ Component: React.ComponentType, open: boolean, overlayProps: Object, onClose: () => void, onRemove: () => void }} props
 */
const OverlayItem = ({Component, open, overlayProps, onClose, onRemove}) => {
    // suppress open=true during SSR/hydration to avoid mismatch
    const [mounted, setMounted] = useState(false)
    useEffect(() => setMounted(true), [])

    // Stable refs so the cleanup can read the latest values without stale closures
    const overlayPropsRef = useRef(overlayProps)
    overlayPropsRef.current = overlayProps
    const openRef = useRef(open)
    openRef.current = open

    // Fire onClose when the overlay is removed programmatically while still open
    useEffect(
        () => () => {
            if (openRef.current) {
                overlayPropsRef.current.onClose?.({source: 'remove'})
            }
        },
        []
    )

    return (
        <Component
            {...overlayProps}
            open={mounted && open}
            onOpenChange={(e) => {
                overlayProps.onOpenChange?.(e)
                if (!e.open) {
                    overlayProps.onClose?.({source: 'close'})
                    onClose()
                }
            }}
            onExitComplete={() => {
                overlayProps.onExitComplete?.()
                if (!openRef.current) {
                    onRemove()
                }
            }}
        />
    )
}

OverlayItem.propTypes = {
    Component: PropTypes.elementType.isRequired,
    open: PropTypes.bool,
    overlayProps: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired
}

/**
 * Renders all active overlays and clears them on route change.
 * @param {{ overlays: Map<string, OverlayEntry>, dispatch: React.Dispatch }} props
 */
const OverlayViewport = ({overlays, dispatch}) => {
    const {pathname} = useLocation()
    useEffect(() => {
        dispatch({type: REMOVE_ALL})
    }, [pathname, dispatch])

    return [...overlays.values()].map(({id, Component, props, open}) => (
        <OverlayItem
            key={id}
            Component={Component}
            open={open}
            overlayProps={props}
            onClose={() => dispatch({type: CLOSE, id})}
            onRemove={() => dispatch({type: REMOVE, id})}
        />
    ))
}

OverlayViewport.propTypes = {
    overlays: PropTypes.instanceOf(Map).isRequired,
    dispatch: PropTypes.func.isRequired
}

/**
 * App-level provider that owns all overlay state.
 * Must wrap any tree that uses `createOverlayType` hooks.
 * @param {{ children: React.ReactNode }} props
 */
export const OverlayProvider = ({children}) => {
    const [overlays, dispatch] = useReducer(overlaysReducer, new Map())

    return (
        <OverlayDispatchContext.Provider value={dispatch}>
            <OverlayStateContext.Provider value={overlays}>
                {children}
                <OverlayViewport overlays={overlays} dispatch={dispatch} />
            </OverlayStateContext.Provider>
        </OverlayDispatchContext.Provider>
    )
}

/**
 * Locks Lenis scroll whenever any overlay is open.
 * Must be rendered INSIDE a `LenisProvider` so `useLenis()` is available,
 * and INSIDE `OverlayProvider` (or a descendant) so `OverlayStateContext` is available.
 *
 * @example
 * // In each page that has both LenisProvider and overlays:
 * <LenisProvider>
 *   <OverlayScrollLock />
 *   {children}
 * </LenisProvider>
 */
export const OverlayScrollLock = () => {
    const overlays = useContext(OverlayStateContext)

    if (process.env.NODE_ENV !== 'production' && !overlays) {
        throw new Error('OverlayScrollLock must be rendered inside <OverlayProvider>')
    }

    const hasOpen = useMemo(
        () => (overlays ? [...overlays.values()].some((o) => o.open) : false),
        [overlays]
    )
    useLenisScrollLock(hasOpen)
    return null
}

OverlayProvider.propTypes = {
    children: PropTypes.node
}

let _typeCount = 0

/**
 * @typedef {{ open: (props?: Object, options?: { mode?: 'stack' }) => void, close: () => void, remove: () => void }} OverlayControls
 */

/**
 * @typedef {{ useOverlay: (instanceId?: string | number) => OverlayControls, useIsOpen: (instanceId?: string | number) => boolean, preload: () => void }} OverlayType
 */

/**
 * Binds a lazy-loaded component to the overlay system.
 * Returns named hook functions — destructure at the overlay module level
 * so ESLint `rules-of-hooks` can enforce correct call-site usage.
 *
 * @param {React.ComponentType & { preload?: () => void }} Component - loadable component
 * @returns {OverlayType}
 *
 * @example
 * // myOverlay/index.jsx
 * const Loadable = loadable(() => import('./internal'))
 * export const { useOverlay: useMyOverlay, useIsOpen: useIsMyOverlayOpen } = createOverlayType(Loadable)
 */
export const createOverlayType = (Component) => {
    const ns = `ot${_typeCount++}`

    /**
     * Returns stable `open`, `close`, `remove` callbacks for this overlay instance.
     * @param {string | number} [instanceId] - use to manage multiple independent instances of the same overlay type
     * @returns {OverlayControls}
     */
    function useOverlay(instanceId) {
        const id = instanceId != null ? `${ns}:${instanceId}` : ns
        const dispatch = useContext(OverlayDispatchContext)

        if (process.env.NODE_ENV !== 'production' && !dispatch) {
            throw new Error('createOverlayType: use() called outside of <OverlayProvider>')
        }

        const open = useCallback(
            (props, {mode} = {}) =>
                dispatch({
                    type: OPEN,
                    id,
                    Component,
                    props: props ?? {},
                    exclusive: mode !== 'stack'
                }),
            [dispatch, id]
        )
        const close = useCallback(() => dispatch({type: CLOSE, id}), [dispatch, id])
        const remove = useCallback(() => dispatch({type: REMOVE, id}), [dispatch, id])

        return {open, close, remove}
    }

    /**
     * Returns whether this overlay instance is currently open.
     * Subscribes only to `OverlayStateContext` — avoids re-rendering dispatch consumers.
     * @param {string | number} [instanceId]
     * @returns {boolean}
     */
    function useIsOpen(instanceId) {
        const id = instanceId != null ? `${ns}:${instanceId}` : ns
        const state = useContext(OverlayStateContext)
        return Boolean(state?.get(id)?.open)
    }

    return {
        useOverlay,
        useIsOpen,
        preload: () => Component.preload?.()
    }
}

/**
 * Provides `closeAll` and `removeAll` dispatch actions.
 * @returns {{ closeAll: () => void, removeAll: () => void }}
 */
export const useOverlayActions = () => {
    const dispatch = useContext(OverlayDispatchContext)

    if (process.env.NODE_ENV !== 'production' && !dispatch) {
        throw new Error('useOverlayActions: must be called inside <OverlayProvider>')
    }

    return useMemo(
        () => ({
            closeAll: () => dispatch({type: CLOSE_ALL}),
            removeAll: () => dispatch({type: REMOVE_ALL})
        }),
        [dispatch]
    )
}
